import { ComponentChildren, CSSProperties } from "preact";
import { forwardRef } from "preact/compat";
import { useEffect, useState } from "preact/hooks";
import QRCode from "react-qr-code";
import { BarLoader } from "react-spinners";
import { toast } from "react-hot-toast";

type ButtonProps = {
  onClick: (e: preact.TargetedMouseEvent<HTMLButtonElement>) => void;
  children: ComponentChildren;
  style?: CSSProperties;
  disabled?: boolean;
  className?: string;
  onMouseDown?: (e: preact.TargetedMouseEvent<HTMLButtonElement>) => void;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ style, children, disabled, onClick, onMouseDown, className }, ref) => (
    <button
      className={"button " + (className || "")}
      type="button"
      onMouseDown={onMouseDown}
      style={style}
      disabled={disabled}
      ref={ref}
      onClick={onClick}
    >
      {children}
    </button>
  ),
);

export const ErrorMessage = ({ error }: { error: { message: string } }) => {
  return (
    <div
      style={{
        display: "flex",
        flexGrow: 1,
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        gap: 5,
      }}
    >
      <div>
        Uh oh. An error has occurred. Try again later or talk to us.
      </div>
      <code>{error.message}</code>
    </div>
  );
};

export const LoadingIndication = ({ text }: { text: string }) => (
  <div
    style={{
      display: "flex",
      flexGrow: 1,
      justifyContent: "center",
      alignItems: "center",
      gap: 5,
      flexDirection: "column",
    }}
  >
    <div>{text}</div>
    <BarLoader color="white" />
  </div>
);

export const QrCode = ({ url, size }: { size: number; url: string }) => (
  // @ts-expect-error lib has not typing
  <QRCode
    size={size}
    style={{ border: "solid 1px white" }}
    value={url}
  />
);

type QrWithTimerProps = {
  url: string;
  size: number;
  issuedAt: number;
};

const durationSec = 60;

export const QrWithTimer = ({ url, size, issuedAt }: QrWithTimerProps) => {
  const computeLeft = () => {
    if (!issuedAt) return durationSec;
    const elapsed = Math.floor((Date.now() - issuedAt) / 1000);
    return Math.max(0, Math.min(durationSec, durationSec - elapsed));
  };

  const [left, setLeft] = useState<number>(computeLeft());

  useEffect(() => {
    // Recompute immediately and set an anchored ticking interval
    setLeft(computeLeft());
    const id = setInterval(() => {
      setLeft(computeLeft());
    }, 1000);
    return () => clearInterval(id);
    // url is included so a new QR resets the countdown, also react to issuedAt/duration changes
  }, [url, issuedAt, durationSec]);

  const pct = Math.max(0, Math.min(100, (left / durationSec) * 100));

  return (
    <div
      style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <QrCode size={size} url={url} />
      <div style={{ fontSize: 12, opacity: 0.8, marginTop: 6 }}>
        Refreshes in {left}s
      </div>
      <div
        style={{
          height: 4,
          width: size,
          background: "rgba(255,255,255,0.15)",
          borderRadius: 2,
          overflow: "hidden",
          marginTop: 4,
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${pct}%`,
            background: "var(--primary)",
            transition: "width 0.2s linear",
          }}
        />
      </div>
    </div>
  );
};

type SecretTokenProps = {
  token: string;
};

export const SecretToken = ({ token }: SecretTokenProps) => {
  const [isVisible, setIsVisible] = useState(false);

  const hiddenPillStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "var(--surface-alt)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius)",
    padding: "2px 8px",
    fontSize: 12,
    fontFamily: "inherit",
    letterSpacing: "normal",
  } as const;

  return (
    <div
      style={{
        fontSize: 14,
        opacity: 0.85,
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
      }}
    >
      <span>Secret Token:</span>
      {isVisible
        ? (
          <span
            style={{
              fontFamily: "monospace",
              letterSpacing: "normal",
              // Avoid pushing action buttons to the far right
              // by not allowing the token text to flex-grow
              // Keep it shrinkable with ellipsis
              minWidth: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {token}
          </span>
        )
        : <span style={hiddenPillStyle}>🔒 Hidden</span>}
      <div style={{ display: "inline-flex", gap: 6 }}>
        <Button
          className="secondary"
          style={{ fontSize: 10, padding: "2px 6px" }}
          onClick={() => setIsVisible(!isVisible)}
        >
          {isVisible ? "🙈 Hide" : "👁 Show"}
        </Button>
        <Button
          className="secondary"
          style={{ fontSize: 10, padding: "2px 6px" }}
          onClick={() => {
            navigator.clipboard.writeText(token);
            toast.success("Token copied to clipboard");
          }}
        >
          📋 Copy
        </Button>
      </div>
    </div>
  );
};

export const RelatedServices = () => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: 16,
      marginTop: 40,
      padding: 24,
      borderTop: "1px solid var(--border)",
      width: "100%",
      maxWidth: 500,
    }}
  >
    <h3
      style={{
        margin: 0,
        color: "var(--text)",
        fontSize: "1.1em",
        textAlign: "center",
      }}
    >
      You might be interested in these services as well
    </h3>
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <a
        href="https://view-chat.com"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          padding: 16,
          border: "1px solid var(--border)",
          borderRadius: "var(--radius)",
          background: "var(--surface-alt)",
          textDecoration: "none",
          transition: "all 0.2s",
          cursor: "pointer",
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.background = "#272c33";
          e.currentTarget.style.borderColor = "var(--primary)";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.background = "var(--surface-alt)";
          e.currentTarget.style.borderColor = "var(--border)";
        }}
      >
        <div
          style={{ color: "var(--primary)", fontWeight: 600, fontSize: "1em" }}
        >
          👀 View-Chat.com
        </div>
        <div style={{ color: "var(--text-muted)", fontSize: "0.9em" }}>
          View and review AI agent conversations conveniently for QA and quality
          assurance
        </div>
      </a>
      <a
        href="https://prompt2bot.com"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          padding: 16,
          border: "1px solid var(--border)",
          borderRadius: "var(--radius)",
          background: "var(--surface-alt)",
          textDecoration: "none",
          transition: "all 0.2s",
          cursor: "pointer",
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.background = "#272c33";
          e.currentTarget.style.borderColor = "var(--primary)";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.background = "var(--surface-alt)";
          e.currentTarget.style.borderColor = "var(--border)";
        }}
      >
        <div
          style={{ color: "var(--primary)", fontWeight: 600, fontSize: "1em" }}
        >
          🤖 Prompt2Bot.com
        </div>
        <div style={{ color: "var(--text-muted)", fontSize: "0.9em" }}>
          Create powerful AI agents using just a prompt for various needs and
          use cases
        </div>
      </a>
    </div>
  </div>
);
